import React from "react";
import FormDialog from "./FormDialog";
import RoundedButton from "../buttons/RoundedButton";

const ICON_COLOR_PRESETS = {
  red: {
    background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
    boxShadow: "0 4px 12px rgba(220, 38, 38, 0.15)",
    color: "#dc2626",
  },
  blue: {
    background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
    boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
    color: "#2563eb",
  },
  green: {
    background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
    boxShadow: "0 4px 12px rgba(22, 163, 74, 0.15)",
    color: "#16a34a",
  },
  yellow: {
    background: "linear-gradient(135deg, #fef9c3 0%, #fde68a 100%)",
    boxShadow: "0 4px 12px rgba(202, 138, 4, 0.15)",
    color: "#ca8a04",
  },
};

/**
 * @typedef {Object} ConfirmDialogProps
 * @property {boolean} visible
 * @property {() => void} onClose
 * @property {() => void} onConfirm
 * @property {boolean} [loading]
 * @property {React.ReactNode} title
 * @property {React.ReactNode} [description]
 * @property {string} [confirmLabel]
 * @property {string} [cancelLabel]
 * @property {"primary" | "secondary" | "success" | "danger" | "warning"} [confirmColor]
 * @property {React.ReactNode} [icon]
 * @property {string} [width]
 * @property {boolean} [disableCloseWhileLoading]
 * @property {React.ReactNode} [children]
 * @property {"red" | "blue" | "green"} [iconBackgroundColor]
 */

/** @param {ConfirmDialogProps} props */

const ConfirmDialog = ({
  visible,
  onClose,
  onConfirm,
  loading = false,
  title,
  description,
  confirmLabel = "Aceptar",
  cancelLabel = "Cancelar",
  confirmColor = "primary",
  icon,
  width = "500px",
  disableCloseWhileLoading = true,
  children,
  iconBackgroundColor = "blue",
  viewButtons = true
}) => {
  const iconStylePreset =
    ICON_COLOR_PRESETS[iconBackgroundColor] || ICON_COLOR_PRESETS.blue;

  return (
    <FormDialog
      visible={visible}
      width={width}
      closeDialog={() => {
        if (disableCloseWhileLoading && loading) return;
        onClose();
      }}
      footer={viewButtons ? (
        <>
          <RoundedButton
            color="secondary"
            label={cancelLabel}
            disabled={loading}
            handleClick={onClose}
          />

          <RoundedButton
            color={confirmColor}
            label={loading ? "Procesando..." : confirmLabel}
            disabled={loading}
            handleClick={onConfirm}
          />
        </>)
      : <></>}
    >
      <div style={{ textAlign: "center" }}>
        {icon && (
          <div
            style={{
              width: 70,
              height: 70,
              background: iconStylePreset.background,
              borderRadius: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
              fontSize: 36,
              boxShadow: iconStylePreset.boxShadow,
              color: iconStylePreset.color,
            }}
          >
            {icon}
          </div>
        )}

        <h5
          style={{
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 12,
            letterSpacing: "-0.5px",
          }}
        >
          {title}
        </h5>

        {description && (
          <p
            style={{
              color: "#6b7280",
              fontSize: 14,
              lineHeight: 1.7,
              margin: 0,
              fontWeight: 500,
            }}
          >
            {description}
          </p>
        )}

        {children}
      </div>
    </FormDialog>
  );
};

export default ConfirmDialog;